import React, { useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ClockIcon, LanguagesIcon, TargetIcon, ZapIcon } from "lucide-react";
import { useLearner } from "../../contexts/LearnerContext";
import { useCountUp } from "../../hooks/useCountUp";
import { BrutalButton } from "../ui/BrutalButton";
import { spring } from "../../utils/motion";
interface LessonCompleteProps {
  courseId: string;
  continueTo: string;
  xp: number;
  accuracy: number;
  seconds: number;
  translatorItems: number;
  advance: boolean;
  title: string;
}
const stickers = [{
  word: "¡Genial!",
  x: -150,
  y: -70,
  r: -12,
  tone: "bg-accent text-night"
}, {
  word: "¡Bravo!",
  x: 150,
  y: -80,
  r: 10,
  tone: "bg-surface text-ink"
}, {
  word: "Perfecto",
  x: -170,
  y: 40,
  r: 8,
  tone: "bg-brand-tint text-ink"
}, {
  word: "Muy bien",
  x: 165,
  y: 45,
  r: -8,
  tone: "bg-accent text-night"
}, {
  word: "¡Olé!",
  x: 0,
  y: -125,
  r: -4,
  tone: "bg-surface text-ink"
}];
function Stat({
  icon: Icon,
  label,
  value,
  suffix
}: {
  icon: typeof ZapIcon;
  label: string;
  value: number;
  suffix?: string;
}) {
  const {
    ref,
    value: shown
  } = useCountUp(value, 1.2);
  return <div className="rounded-card border-3 border-line bg-surface p-4 text-left shadow-brutal-sm" data-id="element-317" data-mp="3smsfbcxmep92">
      <dt className="flex items-center gap-1.5 text-sm font-semibold text-ink-2" data-id="element-318" data-mp="b9yfh5v2u5vf">
        <Icon className="h-4 w-4" aria-hidden="true" data-id="element-319" data-mp="6g3zodileb1m" /> {label}
      </dt>
      <dd className="mt-1 text-3xl font-bold tracking-[-0.02em] text-ink" data-id="element-320" data-mp="k7s5y065u5ao">
        <span ref={ref} data-id="element-321" data-mp="fa8kogb1os98">{shown}</span>
        {suffix}
      </dd>
    </div>;
}
export function LessonComplete({
  courseId,
  continueTo,
  xp,
  accuracy,
  seconds,
  translatorItems,
  advance,
  title
}: LessonCompleteProps) {
  const {
    completeLesson
  } = useLearner();
  const reduce = useReducedMotion() ?? false;
  const saved = useRef(false);
  useEffect(() => {
    if (saved.current) return;
    saved.current = true;
    completeLesson(courseId, xp, advance);
  }, [completeLesson, courseId, xp, advance]);
  const minutes = Math.max(1, Math.round(seconds / 60));
  return <div className="pat-dots flex min-h-screen w-full flex-col items-center justify-center bg-paper px-5 py-12 font-sans text-ink" data-id="element-322" data-mp="3kp0jcwzsr0ke">
      <div className="relative flex flex-col items-center" data-id="element-323" data-mp="3pntvakzfqjig">
        {!reduce && stickers.map((s, i) => <motion.span key={s.word} aria-hidden="true" initial={{
        opacity: 0,
        x: 0,
        y: 0,
        scale: 0.6,
        rotate: 0
      }} animate={{
        opacity: 1,
        x: s.x,
        y: s.y,
        scale: 1,
        rotate: s.r
      }} transition={{
        ...spring.bouncy,
        delay: 0.15 + i * 0.05
      }} className={`pointer-events-none absolute left-1/2 top-1/2 -ml-12 -mt-5 hidden whitespace-nowrap rounded-xl border-3 border-line px-3 py-1.5 text-base font-bold shadow-brutal-sm sm:block ${s.tone}`} data-id="element-324" data-mp="3unzkzvm59vxw" data-mpi={i}>
              {s.word}
            </motion.span>)}
        <motion.div initial={reduce ? false : {
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={spring.bouncy} className="tex-ink-dots grid h-28 w-28 place-items-center rounded-panel border-3 border-line bg-brand-tint text-ink shadow-brutal-lg" data-id="element-325" data-mp="r7976ukbmeln">
          <ZapIcon className="h-14 w-14 fill-accent text-ink" strokeWidth={1.75} data-id="element-326" data-mp="21qkpvrv7ffbf" />
        </motion.div>
      </div>

      <h1 className="mt-10 text-center text-4xl font-bold tracking-[-0.02em] md:text-5xl" data-id="element-327" data-mp="1wtly3fz0jkge">Lesson complete</h1>
      <p className="mt-2 text-center text-[17px] text-ink-2" data-id="element-328" data-mp="2pfwwql327bak">{title}</p>

      <dl className="mt-8 grid w-full max-w-lg grid-cols-3 gap-3" data-id="element-329" data-mp="2pgorxusqv70b">
        <Stat icon={ZapIcon} label="XP" value={xp} suffix="" data-id="element-330" data-mp="2ko55s9fzr3jw" />
        <Stat icon={TargetIcon} label="Accuracy" value={accuracy} suffix="%" data-id="element-331" data-mp="2fq3ptxdv3kcn" />
        <Stat icon={ClockIcon} label="Minutes" value={minutes} data-id="element-332" data-mp="2fo0vx29l972n" />
      </dl>

      {translatorItems > 0 && <p className="mt-5 flex max-w-lg items-center gap-2 rounded-xl border-3 border-line bg-accent px-4 py-2.5 text-sm font-semibold text-night" data-id="element-333" data-mp="38erd1yi68jsu">
          <LanguagesIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-334" data-mp="2svyg7k8ba2jc" />
          You practised {translatorItems} {translatorItems === 1 ? "word" : "words"} you saved from the translator.
        </p>}

      <div className="mt-10 flex w-full max-w-lg flex-col gap-3 sm:flex-row" data-id="element-335" data-mp="2j04uba8pcwow">
        <BrutalButton to={continueTo} size="lg" full data-id="element-336" data-mp="2nypx9hfc9uh1">
          Continue path
        </BrutalButton>
        <BrutalButton to="/app" variant="surface" size="lg" full data-id="element-337" data-mp="3boai65v1z1sy">
          Dashboard
        </BrutalButton>
      </div>
    </div>;
}